import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Eye, MinusCircle, PlusCircle, ShieldAlert, ShieldCheck } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { api } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const who = (c) => staffLabelText(c?.name, c?.employee_id);

/**
 * Phase 156 — live "what will this mapping do?" panel for the Territory dialog (tax collectors).
 * Debounces the current selection and asks GET /users/{id}/territory/preview; nothing is written.
 * `onChange(impact)` lets the dialog warn before saving when properties would be stranded.
 */
export function TerritoryImpact({ code, userId, wardIds, mohallaIds, enabled = true, onChange }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        wardIds.forEach((w) => params.append("ward_ids", w));
        mohallaIds.forEach((m) => params.append("mohalla_ids", m));
        const r = await api.get(`/t/${code}/users/${userId}/territory/preview?${params.toString()}`);
        if (my !== seq.current) return;
        setImpact(r.data);
        onChange?.(r.data);
      } catch (e) {
        if (my !== seq.current) return;
        setError(e?.response?.data?.detail || "Preview unavailable");
        onChange?.(null);
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, userId, enabled, wardIds.join("|"), mohallaIds.join("|")]);

  if (!enabled) return null;
  if (error) return <p className="text-xs text-muted-foreground" data-testid="territory-impact-error">{error}</p>;
  if (!impact && loading) {
    return (
      <div className="rounded-lg border p-3 space-y-2" data-testid="territory-impact-loading">
        <Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  if (!impact) return null;
  const { before, after, gain, keep, lose, coverage } = impact;
  const changed = after.visible !== before.visible;
  const strand = (lose?.stranded || 0) + (coverage?.new_alerts || []).reduce((s, a) => s + (a.stranded_no_mohalla || 0), 0);
  return (
    <div className={`rounded-lg border p-3 space-y-2 text-xs ${loading ? "opacity-70" : ""}`} data-testid="territory-impact">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-sm flex items-center gap-1.5"><Eye className="h-4 w-4 text-primary" /> Impact on assignments</p>
        <p className="tabular-nums" data-testid="territory-impact-visible">
          Visible to this collector: <span className="font-semibold">{n(before.visible)}</span>
          <ArrowRight className="inline h-3 w-3 mx-1" />
          <span className={`font-semibold ${changed ? (after.visible > before.visible ? "text-emerald-700" : "text-amber-700") : ""}`}>{n(after.visible)}</span>
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 px-2.5 py-2" data-testid="territory-impact-gain">
          <p className="font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-1"><PlusCircle className="h-3.5 w-3.5" /> +{n(gain.total)} will come to them</p>
          <p className="text-muted-foreground mt-0.5">
            {gain.unassigned ? `${n(gain.unassigned)} currently unassigned` : null}
            {gain.unassigned && gain.from_collectors.length ? " · " : null}
            {gain.from_collectors.slice(0, 3).map((c) => `${n(c.count)} from ${who(c)}`).join(" · ")}
            {gain.from_collectors.length > 3 ? ` · +${gain.from_collectors.length - 3} more` : null}
            {!gain.total ? "nothing new — covered properties stay with their current collector" : null}
          </p>
        </div>
        <div className="rounded-md bg-muted/40 border px-2.5 py-2" data-testid="territory-impact-keep">
          <p className="font-medium flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {n(keep)} stay with them</p>
          <p className="text-muted-foreground mt-0.5">already theirs and still inside the new territory</p>
        </div>
        <div className={`rounded-md border px-2.5 py-2 ${lose.total ? "bg-amber-500/5 border-amber-500/20" : "bg-muted/40"}`} data-testid="territory-impact-lose">
          <p className={`font-medium flex items-center gap-1 ${lose.total ? "text-amber-800 dark:text-amber-300" : ""}`}><MinusCircle className="h-3.5 w-3.5" /> −{n(lose.total)} will leave them</p>
          <p className="text-muted-foreground mt-0.5">
            {lose.to_collectors.slice(0, 3).map((c) => `${n(c.count)} to ${who(c)}`).join(" · ")}
            {lose.to_collectors.length > 3 ? ` · +${lose.to_collectors.length - 3} more` : null}
            {lose.stranded ? `${lose.to_collectors.length ? " · " : ""}${n(lose.stranded)} to nobody` : null}
            {!lose.total ? "none" : null}
          </p>
        </div>
      </div>
      {(coverage?.new_alerts || []).length > 0 && (
        <div className="rounded-md border border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20 px-2.5 py-2 space-y-1" data-testid="territory-impact-alerts">
          {coverage.new_alerts.map((a) => (
            <p key={a.ward_id} className="text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span><span className="font-medium">{a.ward_name}</span> will have no whole-ward collector — {n(a.stranded_no_mohalla)} propert{a.stranded_no_mohalla === 1 ? "y" : "ies"} without a mohalla will have no covering collector. Map a whole-ward collector or fill their mohallas (Mohalla Backfill).</span>
            </p>
          ))}
        </div>
      )}
      {(coverage?.cleared_alerts || []).length > 0 && (
        <p className="text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5" data-testid="territory-impact-cleared">
          <ShieldCheck className="h-3.5 w-3.5" /> Clears the coverage alert for {coverage.cleared_alerts.map((a) => a.ward_name).join(", ")}.
        </p>
      )}
      {strand > 0 && !(coverage?.new_alerts || []).length && lose.stranded > 0 && (
        <p className="text-amber-800 dark:text-amber-300 flex items-center gap-1.5" data-testid="territory-impact-strand-note">
          <ShieldAlert className="h-3.5 w-3.5" /> {n(lose.stranded)} propert{lose.stranded === 1 ? "y" : "ies"} will be left without any collector who can see {lose.stranded === 1 ? "it" : "them"}.
        </p>
      )}
    </div>
  );
}
